/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';

import type { LoadingStatus } from '../../primitives/loading-status-indicator/LoadingStatusIndicator';
import { ExpandableLoadingStep } from '../expandable-loading-step/ExpandableLoadingStep';
import { LoadingStep } from '../loading-step/LoadingStep';
import { RovoThinkingIndicator } from '../thinking-indicator/ThinkingIndicator';

// partial copy of Agent type from conversation-assistant-service to avoid extra dependency
type Agent = {
	id: string;
	identity_account_id?: string | null;
	named_id: string;
	name: string;
	creator_type: 'SYSTEM' | 'CUSTOMER' | 'THIRD_PARTY' | 'FORGE' | 'OOTB' | 'REMOTE_A2A';
	external_config_reference?: string;
	icon?: string | null;
};

const isForgeAgent = (agent: Agent) =>
	agent.creator_type === 'THIRD_PARTY' ||
	agent.creator_type === 'FORGE' ||
	(agent.creator_type === 'REMOTE_A2A' && fg('rovo_agent_support_a2a_avatar'));

export type ExpandableTrace = {
	title: string;
	status: LoadingStatus;
	content?: React.ReactNode;
	defaultExpanded?: boolean;
};

type MessageLoadingProps = {
	thinkingText?: string;
	// TODO: adjust the type for traces and handle a more complex state
	traces: ExpandableTrace[];
	agent?: Agent;
	// allow explicit control of thinking indicator visibility
	hideThinkingIndicator?: boolean;
	disableAnimation?: boolean;
	// When true and no agent is provided, shows the default Rovo icon
	// instead of hiding the icon entirely.
	showDefaultIcon?: boolean;
	// whether to apply shimmer and morphing loading iconanimation effect to the thinking text
	animation?: boolean;
	/** Called when the user clicks to expand/collapse a trace step. */
	onTraceInteraction?: (event: { type: 'singleTraceExpandable'; expanded: boolean }) => void;
};

const styles = cssMap({
	container: {},
	stepContainerWithIndicator: { marginLeft: token('space.500') },
	stepContainerWithoutIndicator: { marginLeft: token('space.100') },
});

const ExpandableTraceItem = ({
	trace,
	isLastItem,
	placeholder = false,
	onTraceInteraction,
}: {
	isLastItem: boolean;
	trace: ExpandableTrace;
	placeholder?: boolean;
	onTraceInteraction?: (event: { type: 'singleTraceExpandable'; expanded: boolean }) => void;
}) => {
	const { title, defaultExpanded = false, content = null, status } = trace;

	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const setIsExpandedValue = useCallback(() => {
		setIsExpanded((prev) => !prev);
	}, [setIsExpanded]);

	const commonProps = { title, isLastItem, status };

	return content ? (
		<ExpandableLoadingStep
			isExpanded={isExpanded}
			onClick={setIsExpandedValue}
			onExpand={(expanded) => onTraceInteraction?.({ type: 'singleTraceExpandable', expanded })}
			{...commonProps}
		>
			{content}
		</ExpandableLoadingStep>
	) : (
		<LoadingStep {...commonProps} placeholder={placeholder} />
	);
};

export const MessageLoading = ({
	traces,
	disableAnimation,
	agent,
	hideThinkingIndicator = false,
	showDefaultIcon: showDefaultIconProp,
	animation = false,
	onTraceInteraction,
	...props
}: MessageLoadingProps): JSX.Element => {
	const thinkingText = props.thinkingText ?? '';
	// When animation is enabled and no explicit showDefaultIcon was provided,
	// default to showing the icon so the morphing animation icon renders.
	const showDefaultIcon = showDefaultIconProp ?? animation;

	const agentAvatarOrHiddenOrDefault = agent ? (
		<AgentAvatar
			agentNamedId={agent.external_config_reference ?? agent.named_id}
			agentId={agent.id}
			agentIdentityAccountId={agent.identity_account_id}
			size="small"
			showBorder={false}
			isForgeAgent={
				fg('rovo_agent_support_a2a_avatar')
					? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
					: isForgeAgent(agent)
			}
			forgeAgentIconUrl={agent.icon}
		/>
	) : (
		showDefaultIcon
	);

	const needsPlaceholder = traces?.some((t) => t.content);
	const showThinkingIndicator = props.thinkingText && !hideThinkingIndicator;

	return (
		<Box xcss={styles.container}>
			{showThinkingIndicator ? (
				<RovoThinkingIndicator
					title={thinkingText}
					isAnimated={!disableAnimation}
					icon={agentAvatarOrHiddenOrDefault}
					animation={animation}
				/>
			) : null}
			<Box
				xcss={cx(
					!!showThinkingIndicator
						? styles.stepContainerWithIndicator
						: styles.stepContainerWithoutIndicator,
				)}
			>
				{traces
					? traces.map((trace, i) => {
							const isLastItem = i === traces.length - 1;
							return (
								<ExpandableTraceItem
									key={`${trace.title}-${trace.status}`}
									trace={trace}
									isLastItem={isLastItem}
									placeholder={needsPlaceholder}
									onTraceInteraction={onTraceInteraction}
								/>
							);
						})
					: null}
			</Box>
		</Box>
	);
};
